import { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Box from '@mui/material/Box';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { DrawerItem } from 'helpers/drawer-config';
import { useGlobalContext } from 'context/GlobalContext';

type NavBarDropdownProps = {
  anchorEl: null | HTMLElement;
  open: boolean;
  onClose: () => void;
  submenu: DrawerItem[];
};

/**
 * NavBarDropdown Component
 *
 * Dropdown vertical que muestra submenús del item de navegación.
 * Soporta hasta 3 niveles de navegación con submenús anidados.
 *
 * @component
 * @param {NavBarDropdownProps} props - Las propiedades del componente
 * @returns {JSX.Element} El dropdown de navegación
 */
function NavBarDropdown({
  anchorEl,
  open,
  onClose,
  submenu
}: NavBarDropdownProps): JSX.Element {
  const { hasPerm } = useGlobalContext();
  const { t } = useTranslation('global');
  const navigate = useNavigate();
  const [subMenuAnchor, setSubMenuAnchor] = useState<{
    el: HTMLElement;
    items: DrawerItem[];
  } | null>(null);

  const handleItemClick = (item: DrawerItem) => {
    if (item.path) {
      navigate(item.path);
      onClose();
      setSubMenuAnchor(null);
    }
  };

  const handleSubMenuEnter = (
    event: React.MouseEvent<HTMLLIElement>,
    item: DrawerItem
  ) => {
    if (item.submenu && item.submenu.length > 0) {
      setSubMenuAnchor({
        el: event.currentTarget,
        items: item.submenu
      });
    }
  };

  const handleSubMenuLeave = () => {
    setSubMenuAnchor(null);
  };

  const handleMenuLeave = () => {
    // Pequeño delay para permitir mover el mouse al submenu
    setTimeout(() => {
      if (!subMenuAnchor) {
        onClose();
      }
    }, 100);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        MenuListProps={{
          onMouseLeave: handleMenuLeave,
          sx: { py: 0 }
        }}
        onClose={onClose}
        open={open}
        sx={{
          '& .MuiPaper-root': {
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            borderRadius: '4px',
            mt: 0.5,
            minWidth: 200
          }
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        {submenu
          .filter((item) => hasPerm(item.permissions))
          .map((item) => {
            const hasSubItems = item.submenu && item.submenu.length > 0;

            return (
              <MenuItem
                key={item.key}
                onClick={(e) => {
                  if (!hasSubItems) {
                    handleItemClick(item);
                  } else {
                    handleSubMenuEnter(e, item);
                  }
                }}
                onMouseLeave={hasSubItems ? undefined : handleSubMenuLeave}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <ListItemText
                  primary={t(item?.key)}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    color: '#0E335C'
                  }}
                />
                {hasSubItems && (
                  <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
                    <ChevronRightIcon sx={{ fontSize: 20, color: '#666' }} />
                  </Box>
                )}
              </MenuItem>
            );
          })}
      </Menu>

      {/* Submenu de nivel 3 */}
      {subMenuAnchor && (
        <Menu
          anchorEl={subMenuAnchor.el}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          MenuListProps={{
            onMouseLeave: () => setSubMenuAnchor(null),
            sx: { py: 0 }
          }}
          onClose={() => setSubMenuAnchor(null)}
          open={Boolean(subMenuAnchor)}
          sx={{
            '& .MuiPaper-root': {
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              borderRadius: '4px',
              ml: 0.5,
              minWidth: 200
            }
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
        >
          {subMenuAnchor.items
            .filter((item) => hasPerm(item.permissions))
            .map((item) => (
              <MenuItem
                key={item.key}
                onClick={() => handleItemClick(item)}
                sx={{
                  py: 1.5,
                  px: 2,
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <ListItemText
                  primary={t(item?.key)}
                  primaryTypographyProps={{
                    fontSize: '14px',
                    color: '#0E335C'
                  }}
                />
              </MenuItem>
            ))}
        </Menu>
      )}
    </>
  );
}

export default NavBarDropdown;
